import { useState, useEffect, useCallback } from 'react';
import {
  Modal, Tabs, Card, Stack, Group, Text, Title, Badge, Button, TextInput,
  NumberInput, Select, Textarea, Progress, ThemeIcon, ActionIcon, SimpleGrid,
  Box, Paper, Center, Switch, Image, FileButton, Grid,
  useMantineTheme, Loader
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconCash, IconPlus, IconTrash, IconEdit, IconCheck, IconX, IconUpload,
  IconBell, IconBellRinging, IconHome, IconRepeat,
  IconCreditCard, IconBuildingBank, IconCurrencyBitcoin,
  IconSend
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  financialDocumentsApi,
  ReservationConfirmation,
  ConfirmationPayment,
  ConfirmationReminder,
  PaymentSummary,
} from '@/api/financialDocuments.api';
import dayjs from 'dayjs';

interface Props {
  opened: boolean;
  onClose: () => void;
  confirmation: ReservationConfirmation;
  onStatusChange?: () => void;
}

const CURRENCIES = [
  { value: 'THB', label: 'THB (฿)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'RUB', label: 'RUB (₽)' },
  { value: 'CNY', label: 'CNY (¥)' },
  { value: 'GBP', label: 'GBP (£)' },
];

const PaymentManagementModal = ({ opened, onClose, confirmation, onStatusChange }: Props) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [payments, setPayments] = useState<ConfirmationPayment[]>([]);
  const [reminders, setReminders] = useState<ConfirmationReminder[]>([]);

  // Expected amounts editing (multi-currency)
  const [editingExpectedAmounts, setEditingExpectedAmounts] = useState(false);
  const [expectedAmountsForm, setExpectedAmountsForm] = useState<{ amount: number | string; currency: string }[]>([]);

  // Payment form
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '' as number | string,
    currency: confirmation.currency || 'THB',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    notes: ''
  });

  // Reminder form
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const [reminderForm, setReminderForm] = useState({
    reminder_type: 'days_before' as string,
    days_before: 7 as number | string,
    custom_date: '',
    comment: '',
    // Recurrence
    recurrence_type: 'none' as string,
    recurrence_interval: 1 as number | string,
    recurrence_day_of_week: 1 as number | string,   // 1=Пн
    recurrence_week_of_month: 1 as number | string,  // 1-я неделя
    recurrence_end_date: '',
    recurrence_max_count: '' as number | string,
  });

  // Upload state
  const [uploadingPaymentId, setUploadingPaymentId] = useState<number | null>(null);

  // Files attached during payment creation/edit
  const [pendingFiles, setPendingFiles] = useState<{ file: File; preview: string }[]>([]);

  const fetchAll = useCallback(async () => {
    if (!confirmation.id) return;
    setLoading(true);
    try {
      const [summaryRes, paymentsRes, remindersRes] = await Promise.all([
        financialDocumentsApi.getConfirmationPaymentSummary(confirmation.id),
        financialDocumentsApi.getConfirmationPayments(confirmation.id),
        financialDocumentsApi.getConfirmationReminders(confirmation.id),
      ]);
      setSummary(summaryRes.data.data);
      setPayments(paymentsRes.data.data);
      setReminders(remindersRes.data.data);
    } catch {
      notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [confirmation.id, t]);

  useEffect(() => {
    if (opened) fetchAll();
  }, [opened, fetchAll]);

  // ===== EXPECTED AMOUNTS =====
  const handleSaveExpectedAmounts = async () => {
    try {
      const validAmounts = expectedAmountsForm
        .filter(ea => Number(ea.amount) > 0 && ea.currency)
        .map(ea => ({ amount: Number(ea.amount), currency: ea.currency }));
      await financialDocumentsApi.updateConfirmationExpectedAmounts(confirmation.id, validAmounts);
      notifications.show({ message: t('paymentManagement.messages.expectedAmountsUpdated'), color: 'green', icon: <IconCheck size={18} /> });
      setEditingExpectedAmounts(false);
      fetchAll();
      onStatusChange?.();
    } catch {
      notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
    }
  };

  const handleStartEditingExpectedAmounts = () => {
    if (summary?.expected_amounts && summary.expected_amounts.length > 0) {
      setExpectedAmountsForm(summary.expected_amounts.map(ea => ({
        amount: ea.amount,
        currency: ea.currency
      })));
    } else {
      setExpectedAmountsForm([{ amount: '', currency: confirmation.currency || 'THB' }]);
    }
    setEditingExpectedAmounts(true);
  };

  const handleAddExpectedAmountRow = () => {
    const usedCurrencies = expectedAmountsForm.map(ea => ea.currency);
    const nextCurrency = CURRENCIES.find(c => !usedCurrencies.includes(c.value))?.value || 'THB';
    setExpectedAmountsForm(prev => [...prev, { amount: '', currency: nextCurrency }]);
  };

  const handleRemoveExpectedAmountRow = (index: number) => {
    setExpectedAmountsForm(prev => prev.filter((_, i) => i !== index));
  };

  // ===== PAYMENTS =====
  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      currency: confirmation.currency || 'THB',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'bank_transfer',
      notes: ''
    });
    setEditingPaymentId(null);
    setShowPaymentForm(false);
    pendingFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setPendingFiles([]);
  };

  const handleAddPendingFiles = (files: File[]) => {
    const newFiles = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingFiles(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  const handleSavePayment = async () => {
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) return;
    try {
      let targetPaymentId = editingPaymentId;

      if (editingPaymentId) {
        await financialDocumentsApi.updateConfirmationPayment(confirmation.id, editingPaymentId, {
          amount: Number(paymentForm.amount),
          currency: paymentForm.currency,
          payment_date: paymentForm.payment_date,
          payment_method: paymentForm.payment_method,
          notes: paymentForm.notes || undefined
        });
        notifications.show({ message: t('paymentManagement.messages.paymentUpdated'), color: 'green', icon: <IconCheck size={18} /> });
      } else {
        const res = await financialDocumentsApi.createConfirmationPayment(confirmation.id, {
          amount: Number(paymentForm.amount),
          currency: paymentForm.currency,
          payment_date: paymentForm.payment_date,
          payment_method: paymentForm.payment_method,
          notes: paymentForm.notes || undefined
        });
        targetPaymentId = res.data.data.id;
        notifications.show({ message: t('paymentManagement.messages.paymentCreated'), color: 'green', icon: <IconCheck size={18} /> });
      }

      // Upload pending files if any
      if (pendingFiles.length > 0 && targetPaymentId) {
        const formData = new FormData();
        pendingFiles.forEach((f, i) => formData.append(`file_${i}`, f.file));
        await financialDocumentsApi.uploadPaymentProofFiles(confirmation.id, targetPaymentId, formData);
        notifications.show({ message: t('paymentManagement.messages.filesUploaded'), color: 'green', icon: <IconCheck size={18} /> });
      }

      resetPaymentForm();
      fetchAll();
      onStatusChange?.();
    } catch {
      notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
    }
  };

  const handleEditPayment = (payment: ConfirmationPayment) => {
    setPaymentForm({
      amount: payment.amount,
      currency: payment.currency,
      payment_date: dayjs(payment.payment_date).format('YYYY-MM-DD'),
      payment_method: payment.payment_method,
      notes: payment.notes || ''
    });
    setEditingPaymentId(payment.id);
    setShowPaymentForm(true);
  };

  const handleDeletePayment = (paymentId: number) => {
    modals.openConfirmModal({
      title: t('paymentManagement.payments.deletePayment'),
      children: <Text size="sm">{t('paymentManagement.payments.deletePaymentConfirm')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('paymentManagement.overview.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await financialDocumentsApi.deleteConfirmationPayment(confirmation.id, paymentId);
          notifications.show({ message: t('paymentManagement.messages.paymentDeleted'), color: 'green', icon: <IconCheck size={18} /> });
          fetchAll();
          onStatusChange?.();
        } catch {
          notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
        }
      }
    });
  };

  // ===== FILE UPLOAD =====
  const handleUploadFiles = async (paymentId: number, files: File[]) => {
    if (!files.length) return;
    setUploadingPaymentId(paymentId);
    try {
      const formData = new FormData();
      files.forEach((f, i) => formData.append(`file_${i}`, f));
      await financialDocumentsApi.uploadPaymentProofFiles(confirmation.id, paymentId, formData);
      notifications.show({ message: t('paymentManagement.messages.filesUploaded'), color: 'green', icon: <IconCheck size={18} /> });
      fetchAll();
    } catch {
      notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
    } finally {
      setUploadingPaymentId(null);
    }
  };

  const handleDeleteFile = (paymentId: number, fileId: number) => {
    modals.openConfirmModal({
      title: t('paymentManagement.payments.deletePayment'),
      children: <Text size="sm">{t('paymentManagement.payments.deleteFileConfirm')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('paymentManagement.overview.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await financialDocumentsApi.deletePaymentProofFile(confirmation.id, paymentId, fileId);
          notifications.show({ message: t('paymentManagement.messages.fileDeleted'), color: 'green', icon: <IconCheck size={18} /> });
          fetchAll();
        } catch {
          notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
        }
      }
    });
  };

  // ===== REMINDERS =====
  const resetReminderForm = () => {
    setReminderForm({
      reminder_type: 'days_before', days_before: 7, custom_date: '', comment: '',
      recurrence_type: 'none', recurrence_interval: 1, recurrence_day_of_week: 1,
      recurrence_week_of_month: 1, recurrence_end_date: '', recurrence_max_count: '',
    });
    setEditingReminderId(null);
    setShowReminderForm(false);
  };

  const handleSaveReminder = async () => {
    try {
      const data: any = {
        reminder_type: reminderForm.reminder_type,
        comment: reminderForm.comment || undefined,
        is_enabled: 1,
        // Recurrence
        recurrence_type: reminderForm.recurrence_type || 'none',
        recurrence_interval: reminderForm.recurrence_type !== 'none' && reminderForm.recurrence_interval ? Number(reminderForm.recurrence_interval) : null,
        recurrence_end_date: reminderForm.recurrence_end_date || null,
        recurrence_max_count: reminderForm.recurrence_max_count !== '' ? Number(reminderForm.recurrence_max_count) : null,
      };

      if (reminderForm.reminder_type === 'days_before') {
        data.days_before = Number(reminderForm.days_before);
      } else if (reminderForm.reminder_type === 'custom_date') {
        data.custom_date = reminderForm.custom_date;
      }

      // Recurrence-specific fields
      if (reminderForm.recurrence_type === 'weekly' || reminderForm.recurrence_type === 'monthly_by_weekday') {
        data.recurrence_day_of_week = Number(reminderForm.recurrence_day_of_week);
      }
      if (reminderForm.recurrence_type === 'monthly_by_weekday') {
        data.recurrence_week_of_month = Number(reminderForm.recurrence_week_of_month);
      }

      if (editingReminderId) {
        await financialDocumentsApi.updateConfirmationReminder(confirmation.id, editingReminderId, data);
        notifications.show({ message: t('paymentManagement.messages.reminderUpdated'), color: 'green', icon: <IconCheck size={18} /> });
      } else {
        await financialDocumentsApi.createConfirmationReminder(confirmation.id, data);
        notifications.show({ message: t('paymentManagement.messages.reminderCreated'), color: 'green', icon: <IconCheck size={18} /> });
      }
      resetReminderForm();
      fetchAll();
    } catch {
      notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
    }
  };

  const handleQuickAddReminder = async (type: string, daysBefore?: number) => {
    try {
      const data: any = { reminder_type: type, is_enabled: 1 };
      if (type === 'days_before' && daysBefore) data.days_before = daysBefore;
      await financialDocumentsApi.createConfirmationReminder(confirmation.id, data);
      notifications.show({ message: t('paymentManagement.messages.reminderCreated'), color: 'green', icon: <IconCheck size={18} /> });
      fetchAll();
    } catch {
      notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
    }
  };

  const handleToggleReminder = async (reminder: ConfirmationReminder) => {
    try {
      await financialDocumentsApi.updateConfirmationReminder(confirmation.id, reminder.id, {
        is_enabled: reminder.is_enabled ? 0 : 1
      });
      fetchAll();
    } catch {
      notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
    }
  };

  const handleEditReminder = (reminder: ConfirmationReminder) => {
    setReminderForm({
      reminder_type: reminder.reminder_type,
      days_before: reminder.days_before || 7,
      custom_date: reminder.custom_date ? dayjs(reminder.custom_date).format('YYYY-MM-DD') : '',
      comment: reminder.comment || '',
      recurrence_type: (reminder as any).recurrence_type || 'none',
      recurrence_interval: (reminder as any).recurrence_interval || 1,
      recurrence_day_of_week: (reminder as any).recurrence_day_of_week ?? 1,
      recurrence_week_of_month: (reminder as any).recurrence_week_of_month || 1,
      recurrence_end_date: (reminder as any).recurrence_end_date ? dayjs((reminder as any).recurrence_end_date).format('YYYY-MM-DD') : '',
      recurrence_max_count: (reminder as any).recurrence_max_count ?? '',
    });
    setEditingReminderId(reminder.id);
    setShowReminderForm(true);
  };

  const handleDeleteReminder = (reminderId: number) => {
    modals.openConfirmModal({
      title: t('paymentManagement.reminders.deleteReminder'),
      children: <Text size="sm">{t('paymentManagement.reminders.deleteReminderConfirm')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('paymentManagement.overview.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await financialDocumentsApi.deleteConfirmationReminder(confirmation.id, reminderId);
          notifications.show({ message: t('paymentManagement.messages.reminderDeleted'), color: 'green', icon: <IconCheck size={18} /> });
          fetchAll();
        } catch {
          notifications.show({ message: t('paymentManagement.messages.error'), color: 'red' });
        }
      }
    });
  };

  // ===== HELPERS =====
  const getStatusColor = (status: string) => {
    if (status === 'fully_paid') return 'green';
    if (status === 'partially_paid') return 'yellow';
    return 'red';
  };

  const getStatusLabel = (status: string) => {
    if (status === 'fully_paid') return t('paymentManagement.status.fullyPaid');
    if (status === 'partially_paid') return t('paymentManagement.status.partiallyPaid');
    return t('paymentManagement.status.notPaid');
  };

  const getMethodIcon = (method: string) => {
    if (method === 'bank_transfer') return <IconBuildingBank size={16} />;
    if (method === 'cash') return <IconCash size={16} />;
    if (method === 'crypto') return <IconCurrencyBitcoin size={16} />;
    if (method === 'card') return <IconCreditCard size={16} />;
    return <IconCash size={16} />;
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${amount.toLocaleString()} ${currency}`;
  };

  const calculateNights = () => {
    if (!confirmation.arrival_date || !confirmation.departure_date) return 0;
    return dayjs(confirmation.departure_date).diff(dayjs(confirmation.arrival_date), 'day');
  };

  // ===== RENDER =====
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <ThemeIcon size="lg" radius="md" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}>
            <IconCash size={20} />
          </ThemeIcon>
          <Box>
            <Title order={4}>{t('paymentManagement.title')}</Title>
            <Text size="xs" c="dimmed">{confirmation.confirmation_number}</Text>
          </Box>
        </Group>
      }
      size="xl"
      fullScreen={isMobile}
      centered
      styles={{
        body: { padding: isMobile ? 12 : 24 },
      }}
    >
      {loading && !summary ? (
        <Center py="xl"><Loader /></Center>
      ) : (
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List grow>
            <Tabs.Tab value="overview" leftSection={<IconCash size={16} />}>
              {t('paymentManagement.tabs.overview')}
            </Tabs.Tab>
            <Tabs.Tab value="payments" leftSection={<IconCreditCard size={16} />} rightSection={
              payments.length > 0 ? <Badge size="xs" variant="filled" circle>{payments.length}</Badge> : undefined
            }>
              {t('paymentManagement.tabs.payments')}
            </Tabs.Tab>
            <Tabs.Tab value="reminders" leftSection={<IconBell size={16} />} rightSection={
              reminders.length > 0 ? <Badge size="xs" variant="filled" circle>{reminders.length}</Badge> : undefined
            }>
              {t('paymentManagement.tabs.reminders')}
            </Tabs.Tab>
          </Tabs.List>

          {/* ===== OVERVIEW TAB ===== */}
          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              {/* Booking Info */}
              <Card withBorder radius="md" padding="md">
                <Stack gap="sm">
                  <Group gap="xs">
                    <ThemeIcon size="md" variant="light" color="blue" radius="md">
                      <IconHome size={16} />
                    </ThemeIcon>
                    <Text fw={600} size="sm">{t('paymentManagement.overview.bookingInfo')}</Text>
                  </Group>
                  <Grid gutter="sm">
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Text size="xs" c="dimmed">{t('paymentManagement.overview.property')}</Text>
                      <Text size="sm" fw={500}>{confirmation.property_name || '-'}</Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 3 }}>
                      <Text size="xs" c="dimmed">{t('paymentManagement.overview.arrival')}</Text>
                      <Text size="sm" fw={500}>{confirmation.arrival_date ? dayjs(confirmation.arrival_date).format('DD.MM.YYYY') : '-'}</Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 3 }}>
                      <Text size="xs" c="dimmed">{t('paymentManagement.overview.departure')}</Text>
                      <Text size="sm" fw={500}>{confirmation.departure_date ? dayjs(confirmation.departure_date).format('DD.MM.YYYY') : '-'}</Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 4 }}>
                      <Text size="xs" c="dimmed">{t('paymentManagement.overview.guests')}</Text>
                      <Text size="sm" fw={500}>
                        {confirmation.guests?.map(g => g.guest_name).filter(Boolean).join(', ') || `${confirmation.num_guests || 0}`}
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 4 }}>
                      <Text size="xs" c="dimmed">{t('paymentManagement.overview.nights')}</Text>
                      <Text size="sm" fw={500}>{calculateNights()}</Text>
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>

              {/* Payment Progress */}
              <Card withBorder radius="md" padding="md">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <ThemeIcon size="md" variant="light" color="teal" radius="md">
                        <IconCash size={16} />
                      </ThemeIcon>
                      <Text fw={600} size="sm">{t('paymentManagement.overview.paymentProgress')}</Text>
                    </Group>
                    {summary && (
                      <Badge
                        size="lg"
                        color={getStatusColor(summary.payment_status)}
                        variant="light"
                      >
                        {getStatusLabel(summary.payment_status)}
                      </Badge>
                    )}
                  </Group>

                  {summary && (
                    <Paper p="sm" radius="md" withBorder>
                      <Group justify="space-between" mb="xs">
                        <Text size="xs" c="dimmed" fw={600}>{t('paymentManagement.overview.expectedAmounts')}</Text>
                        {!editingExpectedAmounts && (
                          <ActionIcon size="xs" variant="subtle" onClick={handleStartEditingExpectedAmounts}>
                            <IconEdit size={12} />
                          </ActionIcon>
                        )}
                      </Group>

                      {editingExpectedAmounts ? (
                        <Stack gap="xs">
                          {expectedAmountsForm.map((ea, index) => (
                            <Group key={index} gap="xs">
                              <NumberInput
                                size="xs"
                                value={ea.amount}
                                onChange={(v) => {
                                  setExpectedAmountsForm(prev => prev.map((item, i) =>
                                    i === index ? { ...item, amount: v } : item
                                  ));
                                }}
                                min={0}
                                decimalScale={2}
                                style={{ flex: 1 }}
                                placeholder="0.00"
                              />
                              <Select
                                size="xs"
                                value={ea.currency}
                                onChange={(v) => {
                                  setExpectedAmountsForm(prev => prev.map((item, i) =>
                                    i === index ? { ...item, currency: v || 'THB' } : item
                                  ));
                                }}
                                data={CURRENCIES}
                                style={{ width: 130 }}
                              />
                              <ActionIcon
                                size="sm"
                                color="red"
                                variant="light"
                                onClick={() => handleRemoveExpectedAmountRow(index)}
                                disabled={expectedAmountsForm.length <= 1}
                              >
                                <IconTrash size={12} />
                              </ActionIcon>
                            </Group>
                          ))}
                          <Group gap="xs">
                            <Button
                              size="compact-xs"
                              variant="light"
                              leftSection={<IconPlus size={12} />}
                              onClick={handleAddExpectedAmountRow}
                              disabled={expectedAmountsForm.length >= CURRENCIES.length}
                            >
                              {t('paymentManagement.overview.addCurrency')}
                            </Button>
                          </Group>
                          <Group justify="flex-end" gap="xs">
                            <Button size="xs" variant="light" color="gray" onClick={() => setEditingExpectedAmounts(false)}>
                              {t('paymentManagement.overview.cancel')}
                            </Button>
                            <Button size="xs" color="green" leftSection={<IconCheck size={14} />} onClick={handleSaveExpectedAmounts}>
                              {t('paymentManagement.overview.save')}
                            </Button>
                          </Group>
                        </Stack>
                      ) : (
                        <Stack gap="sm">
                          {/* Currencies with expected amounts */}
                          {summary.currency_statuses && summary.currency_statuses
                            .filter(cs => cs.expected > 0)
                            .map(cs => (
                              <Box key={cs.currency}>
                                <Group justify="space-between" mb={4}>
                                  <Group gap="xs">
                                    <Text size="sm" fw={600}>{cs.currency}</Text>
                                    <Text size="xs" c="dimmed">
                                      {formatCurrency(cs.paid, cs.currency)} / {formatCurrency(cs.expected, cs.currency)}
                                    </Text>
                                  </Group>
                                  <Badge
                                    size="sm"
                                    color={cs.is_fully_paid ? 'green' : cs.paid > 0 ? 'yellow' : 'red'}
                                    variant="light"
                                  >
                                    {cs.is_fully_paid
                                      ? t('paymentManagement.status.fullyPaid')
                                      : `${t('paymentManagement.overview.remaining')}: ${formatCurrency(cs.remaining, cs.currency)}`
                                    }
                                  </Badge>
                                </Group>
                                <Progress
                                  value={cs.expected > 0 ? Math.min(100, (cs.paid / cs.expected) * 100) : 0}
                                  size="md"
                                  radius="xl"
                                  color={cs.is_fully_paid ? 'green' : cs.paid > 0 ? 'yellow' : 'gray'}
                                  striped={!cs.is_fully_paid && cs.paid > 0}
                                  animated={!cs.is_fully_paid && cs.paid > 0}
                                />
                              </Box>
                            ))
                          }

                          {/* Currencies with payments but no expected amount */}
                          {summary.currency_statuses && summary.currency_statuses
                            .filter(cs => cs.expected === 0 && cs.paid > 0)
                            .map(cs => (
                              <Group key={cs.currency} justify="space-between">
                                <Group gap="xs">
                                  <Text size="sm" fw={600}>{cs.currency}</Text>
                                  <Badge size="xs" variant="light" color="gray">{t('paymentManagement.overview.noTarget')}</Badge>
                                </Group>
                                <Text size="sm" fw={700} c="green">{formatCurrency(cs.paid, cs.currency)}</Text>
                              </Group>
                            ))
                          }

                          {/* No data at all */}
                          {(!summary.currency_statuses || summary.currency_statuses.length === 0) && (
                            <Text size="sm" c="dimmed" ta="center">{t('paymentManagement.overview.noExpectedAmounts')}</Text>
                          )}
                        </Stack>
                      )}
                    </Paper>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* ===== PAYMENTS TAB ===== */}
          <Tabs.Panel value="payments" pt="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={600}>{t('paymentManagement.payments.title')}</Text>
                <Button
                  size="xs"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => { resetPaymentForm(); setShowPaymentForm(true); }}
                  variant="light"
                >
                  {t('paymentManagement.payments.addPayment')}
                </Button>
              </Group>

              {/* Payment Form */}
              {showPaymentForm && (
                <Card withBorder radius="md" padding="md" style={{ borderColor: theme.colors.blue[5] }}>
                  <Stack gap="sm">
                    <Text fw={600} size="sm">
                      {editingPaymentId ? t('paymentManagement.payments.editPayment') : t('paymentManagement.payments.addPayment')}
                    </Text>
                    <Grid gutter="sm">
                      <Grid.Col span={{ base: 6, sm: 4 }}>
                        <NumberInput
                          label={t('paymentManagement.payments.amount')}
                          value={paymentForm.amount}
                          onChange={(v) => setPaymentForm(p => ({ ...p, amount: v }))}
                          min={0}
                          decimalScale={2}
                          required
                          size="sm"
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 6, sm: 3 }}>
                        <Select
                          label={t('paymentManagement.payments.currency')}
                          value={paymentForm.currency}
                          onChange={(v) => setPaymentForm(p => ({ ...p, currency: v || 'THB' }))}
                          data={CURRENCIES}
                          size="sm"
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 6, sm: 3 }}>
                        <TextInput
                          label={t('paymentManagement.payments.date')}
                          type="date"
                          value={paymentForm.payment_date}
                          onChange={(e) => setPaymentForm(p => ({ ...p, payment_date: e.currentTarget.value }))}
                          size="sm"
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 6, sm: 2 }}>
                        <Select
                          label={t('paymentManagement.payments.method')}
                          value={paymentForm.payment_method}
                          onChange={(v) => setPaymentForm(p => ({ ...p, payment_method: v || 'bank_transfer' }))}
                          data={[
                            { value: 'bank_transfer', label: t('paymentManagement.payments.methods.bank_transfer') },
                            { value: 'cash', label: t('paymentManagement.payments.methods.cash') },
                            { value: 'crypto', label: t('paymentManagement.payments.methods.crypto') },
                            { value: 'card', label: t('paymentManagement.payments.methods.card') },
                            { value: 'other', label: t('paymentManagement.payments.methods.other') },
                          ]}
                          size="sm"
                        />
                      </Grid.Col>
                    </Grid>
                    <Textarea
                      label={t('paymentManagement.payments.notes')}
                      placeholder={t('paymentManagement.payments.notesPlaceholder')}
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.currentTarget.value }))}
                      size="sm"
                      rows={2}
                    />

                    {/* File attachment during create/edit */}
                    <Box>
                      <Text size="sm" fw={500} mb={4}>{t('paymentManagement.payments.proofFiles')}</Text>
                      <FileButton
                        onChange={handleAddPendingFiles}
                        accept="image/*"
                        multiple
                      >
                        {(props) => (
                          <Button
                            {...props}
                            size="xs"
                            variant="light"
                            color="violet"
                            leftSection={<IconUpload size={14} />}
                          >
                            {pendingFiles.length > 0
                              ? t('paymentManagement.payments.uploadMore', { count: pendingFiles.length })
                              : t('paymentManagement.payments.uploadProof')
                            }
                          </Button>
                        )}
                      </FileButton>
                      {pendingFiles.length > 0 && (
                        <SimpleGrid cols={{ base: 3, sm: 4, md: 5 }} spacing="xs" mt="xs">
                          {pendingFiles.map((fileObj, index) => (
                            <Box key={index} pos="relative">
                              <Image
                                src={fileObj.preview}
                                alt={fileObj.file.name}
                                radius="sm"
                                h={70}
                                fit="cover"
                              />
                              <ActionIcon
                                size="xs"
                                color="red"
                                variant="filled"
                                radius="xl"
                                pos="absolute"
                                top={2}
                                right={2}
                                onClick={() => handleRemovePendingFile(index)}
                              >
                                <IconX size={10} />
                              </ActionIcon>
                              <Text size="xs" c="dimmed" ta="center" lineClamp={1}>{fileObj.file.name}</Text>
                            </Box>
                          ))}
                        </SimpleGrid>
                      )}
                    </Box>

                    <Group justify="flex-end" gap="xs">
                      <Button variant="light" color="gray" size="xs" onClick={resetPaymentForm}>
                        {t('paymentManagement.overview.cancel')}
                      </Button>
                      <Button size="xs" onClick={handleSavePayment} leftSection={<IconCheck size={16} />}>
                        {t('paymentManagement.overview.save')}
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              )}

              {/* Payment List */}
              {payments.length === 0 && !showPaymentForm ? (
                <Paper p="xl" radius="md" withBorder>
                  <Center>
                    <Stack align="center" gap="md">
                      <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                        <IconCash size={30} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed" ta="center">{t('paymentManagement.payments.noPayments')}</Text>
                      <Text size="xs" c="dimmed" ta="center">{t('paymentManagement.payments.noPaymentsDesc')}</Text>
                    </Stack>
                  </Center>
                </Paper>
              ) : (
                <Stack gap="sm">
                  {payments.map((payment) => (
                    <Card key={payment.id} withBorder radius="md" padding="sm">
                      <Stack gap="xs">
                        <Group justify="space-between" wrap="wrap">
                          <Group gap="xs">
                            <ThemeIcon size="sm" variant="light" color="green" radius="xl">
                              {getMethodIcon(payment.payment_method)}
                            </ThemeIcon>
                            <Text fw={700} size="md" c="green">
                              {formatCurrency(payment.amount, payment.currency)}
                            </Text>
                            <Badge size="xs" variant="light">
                              {t(`paymentManagement.payments.methods.${payment.payment_method}`)}
                            </Badge>
                          </Group>
                          <Group gap={4}>
                            <Text size="xs" c="dimmed">
                              {dayjs(payment.payment_date).format('DD.MM.YYYY')}
                            </Text>
                            <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => handleEditPayment(payment)}>
                              <IconEdit size={14} />
                            </ActionIcon>
                            <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeletePayment(payment.id)}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                        </Group>

                        {payment.notes && (
                          <Text size="xs" c="dimmed" lineClamp={2}>{payment.notes}</Text>
                        )}

                        {payment.created_by_name && (
                          <Text size="xs" c="dimmed">{payment.created_by_name}</Text>
                        )}

                        {/* Proof files */}
                        {payment.files && payment.files.length > 0 && (
                          <SimpleGrid cols={{ base: 3, sm: 4, md: 5 }} spacing="xs">
                            {payment.files.map((file) => (
                              <Box key={file.id} pos="relative">
                                <Image
                                  src={`${import.meta.env.VITE_API_BASE_URL || ''}${file.file_path}`}
                                  alt={file.file_name}
                                  radius="sm"
                                  h={80}
                                  fit="cover"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => window.open(`${import.meta.env.VITE_API_BASE_URL || ''}${file.file_path}`, '_blank')}
                                />
                                <ActionIcon
                                  size="xs"
                                  color="red"
                                  variant="filled"
                                  radius="xl"
                                  pos="absolute"
                                  top={2}
                                  right={2}
                                  onClick={() => handleDeleteFile(payment.id, file.id)}
                                >
                                  <IconX size={10} />
                                </ActionIcon>
                              </Box>
                            ))}
                          </SimpleGrid>
                        )}

                        {/* Upload button */}
                        <FileButton
                          onChange={(files) => handleUploadFiles(payment.id, files)}
                          accept="image/*"
                          multiple
                        >
                          {(props) => (
                            <Button
                              {...props}
                              size="xs"
                              variant="light"
                              color="violet"
                              leftSection={uploadingPaymentId === payment.id ? <Loader size={14} /> : <IconUpload size={14} />}
                              disabled={uploadingPaymentId === payment.id}
                            >
                              {payment.files && payment.files.length > 0
                                ? t('paymentManagement.payments.uploadMore', { count: payment.files.length })
                                : t('paymentManagement.payments.uploadProof')
                              }
                            </Button>
                          )}
                        </FileButton>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </Stack>
          </Tabs.Panel>

          {/* ===== REMINDERS TAB ===== */}
          <Tabs.Panel value="reminders" pt="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="xs">
                  <Text fw={600}>{t('paymentManagement.reminders.title')}</Text>
                  <Badge size="xs" variant="light" color="blue" leftSection={<IconSend size={10} />}>
                    Telegram
                  </Badge>
                </Group>
                <Button
                  size="xs"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => { resetReminderForm(); setShowReminderForm(true); }}
                  variant="light"
                >
                  {t('paymentManagement.reminders.addReminder')}
                </Button>
              </Group>

              {/* Quick Add Presets */}
              <Card withBorder radius="md" padding="sm">
                <Stack gap="xs">
                  <Text size="xs" fw={600} c="dimmed">{t('paymentManagement.reminders.quickAdd')}</Text>
                  <Group gap="xs" wrap="wrap">
                    <Button size="compact-xs" variant="outline" onClick={() => handleQuickAddReminder('check_in_day')}>
                      {t('paymentManagement.reminders.presets.checkInDay')}
                    </Button>
                    <Button size="compact-xs" variant="outline" onClick={() => handleQuickAddReminder('days_before', 3)}>
                      {t('paymentManagement.reminders.presets.threeDaysBefore')}
                    </Button>
                    <Button size="compact-xs" variant="outline" onClick={() => handleQuickAddReminder('days_before', 7)}>
                      {t('paymentManagement.reminders.presets.sevenDaysBefore')}
                    </Button>
                    <Button size="compact-xs" variant="outline" onClick={() => handleQuickAddReminder('days_before', 14)}>
                      {t('paymentManagement.reminders.presets.fourteenDaysBefore')}
                    </Button>
                    <Button size="compact-xs" variant="outline" onClick={() => handleQuickAddReminder('days_before', 30)}>
                      {t('paymentManagement.reminders.presets.thirtyDaysBefore')}
                    </Button>
                  </Group>
                </Stack>
              </Card>

              {/* Reminder Form */}
              {showReminderForm && (
                <Card withBorder radius="md" padding="md" style={{ borderColor: theme.colors.orange[5] }}>
                  <Stack gap="sm">
                    <Text fw={600} size="sm">
                      {editingReminderId ? t('paymentManagement.reminders.editReminder') : t('paymentManagement.reminders.addReminder')}
                    </Text>
                    {/* === TRIGGER === */}
                    <Grid gutter="sm">
                      <Grid.Col span={{ base: 12, sm: 4 }}>
                        <Select
                          label={t('paymentManagement.reminders.type')}
                          value={reminderForm.reminder_type}
                          onChange={(v) => setReminderForm(p => ({ ...p, reminder_type: v || 'days_before' }))}
                          data={[
                            { value: 'check_in_day', label: t('paymentManagement.reminders.types.check_in_day') },
                            { value: 'days_before', label: t('paymentManagement.reminders.types.days_before') },
                            { value: 'custom_date', label: t('paymentManagement.reminders.types.custom_date') },
                          ]}
                          size="sm"
                        />
                      </Grid.Col>
                      {reminderForm.reminder_type === 'days_before' && (
                        <Grid.Col span={{ base: 12, sm: 4 }}>
                          <NumberInput
                            label={t('paymentManagement.reminders.daysBefore')}
                            value={reminderForm.days_before}
                            onChange={(v) => setReminderForm(p => ({ ...p, days_before: v }))}
                            min={1}
                            max={365}
                            size="sm"
                          />
                        </Grid.Col>
                      )}
                      {reminderForm.reminder_type === 'custom_date' && (
                        <Grid.Col span={{ base: 12, sm: 4 }}>
                          <TextInput
                            label={t('paymentManagement.reminders.customDate')}
                            type="date"
                            value={reminderForm.custom_date}
                            onChange={(e) => setReminderForm(p => ({ ...p, custom_date: e.currentTarget.value }))}
                            size="sm"
                          />
                        </Grid.Col>
                      )}
                    </Grid>

                    {/* === RECURRENCE === */}
                    <Box style={{ borderTop: `1px solid ${theme.colors.gray[3]}`, paddingTop: 8 }}>
                      <Group mb={6}>
                        <IconRepeat size={14} color={theme.colors.blue[6]} />
                        <Text size="xs" fw={600} c="dimmed">{t('paymentManagement.reminders.recurrence')}</Text>
                      </Group>
                      <Grid gutter="sm">
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <Select
                            label={t('paymentManagement.reminders.recurrenceType')}
                            value={reminderForm.recurrence_type}
                            onChange={(v) => setReminderForm(p => ({ ...p, recurrence_type: v || 'none' }))}
                            size="sm"
                            data={[
                              { value: 'none', label: t('paymentManagement.reminders.recurrenceTypes.none') },
                              { value: 'every_n_days', label: t('paymentManagement.reminders.recurrenceTypes.every_n_days') },
                              { value: 'weekly', label: t('paymentManagement.reminders.recurrenceTypes.weekly') },
                              { value: 'every_n_weeks', label: t('paymentManagement.reminders.recurrenceTypes.every_n_weeks') },
                              { value: 'monthly_by_date', label: t('paymentManagement.reminders.recurrenceTypes.monthly_by_date') },
                              { value: 'monthly_by_weekday', label: t('paymentManagement.reminders.recurrenceTypes.monthly_by_weekday') },
                              { value: 'every_n_months', label: t('paymentManagement.reminders.recurrenceTypes.every_n_months') },
                              { value: 'yearly', label: t('paymentManagement.reminders.recurrenceTypes.yearly') },
                            ]}
                          />
                        </Grid.Col>

                        {/* Interval (N) */}
                        {['every_n_days', 'every_n_weeks', 'every_n_months'].includes(reminderForm.recurrence_type) && (
                          <Grid.Col span={{ base: 12, sm: 3 }}>
                            <NumberInput
                              label={
                                reminderForm.recurrence_type === 'every_n_days' ? t('paymentManagement.reminders.recurrenceInterval.days') :
                                reminderForm.recurrence_type === 'every_n_weeks' ? t('paymentManagement.reminders.recurrenceInterval.weeks') : t('paymentManagement.reminders.recurrenceInterval.months')
                              }
                              value={reminderForm.recurrence_interval}
                              onChange={(v) => setReminderForm(p => ({ ...p, recurrence_interval: v }))}
                              min={1} max={365} size="sm"
                            />
                          </Grid.Col>
                        )}

                        {/* Day of week */}
                        {['weekly', 'monthly_by_weekday'].includes(reminderForm.recurrence_type) && (
                          <Grid.Col span={{ base: 12, sm: 3 }}>
                            <Select
                              label={t('paymentManagement.reminders.dayOfWeek')}
                              value={String(reminderForm.recurrence_day_of_week)}
                              onChange={(v) => setReminderForm(p => ({ ...p, recurrence_day_of_week: Number(v) }))}
                              size="sm"
                              data={[
                                { value: '1', label: t('paymentManagement.reminders.daysOfWeek.1') },
                                { value: '2', label: t('paymentManagement.reminders.daysOfWeek.2') },
                                { value: '3', label: t('paymentManagement.reminders.daysOfWeek.3') },
                                { value: '4', label: t('paymentManagement.reminders.daysOfWeek.4') },
                                { value: '5', label: t('paymentManagement.reminders.daysOfWeek.5') },
                                { value: '6', label: t('paymentManagement.reminders.daysOfWeek.6') },
                                { value: '0', label: t('paymentManagement.reminders.daysOfWeek.0') },
                              ]}
                            />
                          </Grid.Col>
                        )}

                        {/* Week of month */}
                        {reminderForm.recurrence_type === 'monthly_by_weekday' && (
                          <Grid.Col span={{ base: 12, sm: 3 }}>
                            <Select
                              label={t('paymentManagement.reminders.weekOfMonth')}
                              value={String(reminderForm.recurrence_week_of_month)}
                              onChange={(v) => setReminderForm(p => ({ ...p, recurrence_week_of_month: Number(v) }))}
                              size="sm"
                              data={[
                                { value: '1', label: t('paymentManagement.reminders.weekOfMonthOptions.1') },
                                { value: '2', label: t('paymentManagement.reminders.weekOfMonthOptions.2') },
                                { value: '3', label: t('paymentManagement.reminders.weekOfMonthOptions.3') },
                                { value: '4', label: t('paymentManagement.reminders.weekOfMonthOptions.4') },
                                { value: '5', label: t('paymentManagement.reminders.weekOfMonthOptions.5') },
                              ]}
                            />
                          </Grid.Col>
                        )}
                      </Grid>

                      {/* End conditions (only when recurrence is set) */}
                      {reminderForm.recurrence_type !== 'none' && (
                        <Grid gutter="sm" mt={6}>
                          <Grid.Col span={{ base: 12, sm: 4 }}>
                            <TextInput
                              label={t('paymentManagement.reminders.endDate')}
                              type="date"
                              value={reminderForm.recurrence_end_date}
                              onChange={(e) => setReminderForm(p => ({ ...p, recurrence_end_date: e.currentTarget.value }))}
                              size="sm"
                              placeholder={t('paymentManagement.reminders.endDatePlaceholder')}
                              description={t('paymentManagement.reminders.endDateDesc')}
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 4 }}>
                            <NumberInput
                              label={t('paymentManagement.reminders.maxCount')}
                              value={reminderForm.recurrence_max_count}
                              onChange={(v) => setReminderForm(p => ({ ...p, recurrence_max_count: v }))}
                              min={1} max={9999} size="sm"
                              placeholder={t('paymentManagement.reminders.maxCountPlaceholder')}
                              description={t('paymentManagement.reminders.maxCountDesc')}
                            />
                          </Grid.Col>
                        </Grid>
                      )}
                    </Box>
                    <Textarea
                      label={t('paymentManagement.reminders.comment')}
                      placeholder={t('paymentManagement.reminders.commentPlaceholder')}
                      value={reminderForm.comment}
                      onChange={(e) => setReminderForm(p => ({ ...p, comment: e.currentTarget.value }))}
                      size="sm"
                      rows={2}
                    />
                    <Group justify="flex-end" gap="xs">
                      <Button variant="light" color="gray" size="xs" onClick={resetReminderForm}>
                        {t('paymentManagement.overview.cancel')}
                      </Button>
                      <Button size="xs" onClick={handleSaveReminder} leftSection={<IconCheck size={16} />}>
                        {t('paymentManagement.overview.save')}
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              )}

              {/* Reminder List */}
              {reminders.length === 0 && !showReminderForm ? (
                <Paper p="xl" radius="md" withBorder>
                  <Center>
                    <Stack align="center" gap="md">
                      <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                        <IconBell size={30} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed" ta="center">{t('paymentManagement.reminders.noReminders')}</Text>
                      <Text size="xs" c="dimmed" ta="center">{t('paymentManagement.reminders.noRemindersDesc')}</Text>
                    </Stack>
                  </Center>
                </Paper>
              ) : (
                <Stack gap="sm">
                  {reminders.map((reminder) => (
                    <Card key={reminder.id} withBorder radius="md" padding="sm">
                      <Group justify="space-between" wrap="wrap">
                        <Group gap="xs">
                          <ThemeIcon
                            size="sm"
                            variant="light"
                            color={reminder.is_sent ? 'green' : reminder.is_enabled ? 'orange' : 'gray'}
                            radius="xl"
                          >
                            {reminder.is_sent ? <IconCheck size={14} /> : <IconBellRinging size={14} />}
                          </ThemeIcon>
                          <Box>
                            <Group gap="xs">
                              <Badge size="xs" color="blue" variant="light">
                                {t(`paymentManagement.reminders.types.${reminder.reminder_type}`)}
                              </Badge>
                              {reminder.reminder_type === 'days_before' && reminder.days_before && (
                                <Text size="xs" fw={500}>{reminder.days_before} {t('paymentManagement.reminders.daysBefore').toLowerCase()}</Text>
                              )}
                              {reminder.reminder_type === 'custom_date' && reminder.custom_date && (
                                <Text size="xs" fw={500}>{dayjs(reminder.custom_date).format('DD.MM.YYYY')}</Text>
                              )}
                              {(reminder as any).recurrence_type && (reminder as any).recurrence_type !== 'none' && (
                                <Badge size="xs" color="blue" variant="light" leftSection={<IconRepeat size={10} />}>
                                  {(reminder as any).recurrence_type === 'every_n_days' && t('paymentManagement.reminders.recurrenceBadge.every_n_days').replace('{n}', (reminder as any).recurrence_interval)}
                                  {(reminder as any).recurrence_type === 'weekly' && t('paymentManagement.reminders.recurrenceBadge.weekly')}
                                  {(reminder as any).recurrence_type === 'every_n_weeks' && t('paymentManagement.reminders.recurrenceBadge.every_n_weeks').replace('{n}', (reminder as any).recurrence_interval)}
                                  {(reminder as any).recurrence_type === 'monthly_by_date' && t('paymentManagement.reminders.recurrenceBadge.monthly_by_date')}
                                  {(reminder as any).recurrence_type === 'monthly_by_weekday' && t('paymentManagement.reminders.recurrenceBadge.monthly_by_weekday')}
                                  {(reminder as any).recurrence_type === 'every_n_months' && t('paymentManagement.reminders.recurrenceBadge.every_n_months').replace('{n}', (reminder as any).recurrence_interval)}
                                  {(reminder as any).recurrence_type === 'yearly' && t('paymentManagement.reminders.recurrenceBadge.yearly')}
                                </Badge>
                              )}
                              {(reminder as any).recurrence_sent_count > 0 && (
                                <Text size="xs" c="dimmed">{t('paymentManagement.reminders.sentCount').replace('{count}', (reminder as any).recurrence_sent_count)}</Text>
                              )}
                              {(reminder as any).next_send_date && !reminder.is_sent && (
                                <Text size="xs" c="dimmed">{t('paymentManagement.reminders.nextSend')} {dayjs((reminder as any).next_send_date).format('DD.MM.YYYY')}</Text>
                              )}
                              {reminder.is_sent && (reminder as any).recurrence_type === 'none' ? (
                                <Badge size="xs" color="green" variant="light">{t('paymentManagement.reminders.sent')}</Badge>
                              ) : !reminder.is_sent ? (
                                <Badge size="xs" color="gray" variant="light">{t('paymentManagement.reminders.notSent')}</Badge>
                              ) : null}
                            </Group>
                            {reminder.comment && (
                              <Text size="xs" c="dimmed" mt={2}>{reminder.comment}</Text>
                            )}
                            {reminder.sent_at && (
                              <Text size="xs" c="dimmed">{t('paymentManagement.reminders.sentAt')}: {dayjs(reminder.sent_at).format('DD.MM.YYYY HH:mm')}</Text>
                            )}
                          </Box>
                        </Group>
                        <Group gap={4}>
                          <Switch
                            size="xs"
                            checked={!!reminder.is_enabled}
                            onChange={() => handleToggleReminder(reminder)}
                          />
                          <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => handleEditReminder(reminder)}>
                            <IconEdit size={14} />
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeleteReminder(reminder.id)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}

              <Text size="xs" c="dimmed" ta="center" mt="xs">
                <IconSend size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {t('paymentManagement.reminders.telegramNote')}
              </Text>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
    </Modal>
  );
};

export default PaymentManagementModal;
